import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Collapse,
  Drawer,
  IconButton,
  Link as MuiLink,
  Paper,
  LinearProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CloudUpload,
  Download,
  Error as ErrorIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@mui/icons-material';

import PrimaryPage from '../../Layout/PrimaryPage/index.jsx';
import { ErrorDialog } from '../../Components/Dialogs';
import BulkUploadUploadPanel from './BulkUploadUploadPanel.jsx';
import {
  buildErrorCsvContent,
  buildErrorLines,
  getErrorSamples,
  sanitizeErrorMessage,
} from './bulkUploadErrorSamples';
import useBulkUploadSummaryPolling from '../../hooks/useBulkUploadSummaryPolling';
import {
  downloadBlobFile,
  resolveBulkUploadResultDownload,
  triggerFileDownload,
} from '../../services/bulkUploadDownloadUtils';
import BulkUploadContext from '../../services/bulkUploadService';
import { getBulkUploadTimestamp } from '../../services/bulkUploadTimestamp';
import { upsertUpload, useBulkUploads } from '../../stores/bulkUploadStore';

const PROGRESS_SEGMENT_META = {
  submitting: { label: 'Submitting', color: '#B0B9CF' },
  verifying: { label: 'Verifying', color: '#58B4F0' },
  withAgent: { label: 'With Agent', color: '#165892' },
  verified: { label: 'Approved', color: '#7BB872' },
  rejected: { label: 'Rejected', color: '#CF2E2E' },
  cancelled: { label: 'Canceled', color: '#B0B9CF' },
  errors: { label: 'Errors', color: '#CF2E2E' },
};
const REJECTED_SEGMENT_COLOR_FALLBACK = '#E68231';

const PROGRESS_BAR_ORDER = ['verified', 'rejected', 'cancelled', 'errors', 'withAgent', 'verifying', 'submitting'];
const PROGRESS_LEGEND_ORDER = ['verified', 'rejected', 'cancelled', 'errors', 'withAgent', 'verifying', 'submitting'];
const PROGRESS_TRACK_COLOR = '#E3E8F0';
const SHIMMER_SEGMENT_KEYS = new Set(['verifying', 'withAgent', 'submitting']);
const WITH_AGENT_THRESHOLD_MS = 10 * 60 * 1000;
const SHIMMER_KEYFRAMES = {
  '0%': { transform: 'translateX(-100%)' },
  '100%': { transform: 'translateX(100%)' },
};
const RELATIVE_TIME_FORMATTER = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const ABSOLUTE_DATE_FORMATTER_BASE_OPTIONS = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
};
const ABSOLUTE_DATE_FORMATTER_CURRENT_YEAR = new Intl.DateTimeFormat(
  undefined,
  ABSOLUTE_DATE_FORMATTER_BASE_OPTIONS,
);
const ABSOLUTE_DATE_FORMATTER_WITH_YEAR = new Intl.DateTimeFormat(undefined, {
  ...ABSOLUTE_DATE_FORMATTER_BASE_OPTIONS,
  year: 'numeric',
});

const toSafeCount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 0;
  }
  return Math.floor(parsed);
};

const BulkUpload = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { uploadId: routeUploadId } = useParams();
  const { getBulkUploadDetails, generateBulkUploadArchive } = useContext(BulkUploadContext);
  const [uploadPanelOpen, setUploadPanelOpen] = useState(false);
  const uploads = useBulkUploads();
  const [displayError, setDisplayError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedUploadId, setExpandedUploadId] = useState(null);
  const [downloadMenu, setDownloadMenu] = useState({ anchorEl: null, uploadId: null });
  const [activeDownload, setActiveDownload] = useState({ uploadId: null, kind: null });
  const [dateDisplayByUploadId, setDateDisplayByUploadId] = useState({});
  const activeDownloadRef = useRef(null);
  const rowRefs = useRef(new Map());
  const pendingScrollUploadIdRef = useRef(null);
  const {
    isLoading,
    error: summaryError,
    hasMore,
    isLoadingMore,
    loadMore,
  } = useBulkUploadSummaryPolling({
    detailUploadId: routeUploadId,
  });

  useEffect(() => {
    if (!summaryError) return;
    setErrorMessage('Failed to load uploads. Please try again.');
    setDisplayError(true);
  }, [summaryError]);

  const formatDate = (dateString) => {
    if (!dateString) {
      return '-';
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const absDiffMs = Math.abs(diffMs);
    const minuteMs = 60 * 1000;
    const hourMs = 60 * minuteMs;
    const dayMs = 24 * hourMs;
    const monthMs = 30 * dayMs;
    const yearMs = 365 * dayMs;

    if (absDiffMs < minuteMs) {
      return 'just now';
    }
    if (absDiffMs < hourMs) {
      return RELATIVE_TIME_FORMATTER.format(Math.trunc(diffMs / minuteMs), 'minute');
    }
    if (absDiffMs < dayMs) {
      return RELATIVE_TIME_FORMATTER.format(Math.trunc(diffMs / hourMs), 'hour');
    }
    if (absDiffMs < monthMs) {
      return RELATIVE_TIME_FORMATTER.format(Math.trunc(diffMs / dayMs), 'day');
    }
    if (absDiffMs < yearMs) {
      return RELATIVE_TIME_FORMATTER.format(Math.trunc(diffMs / monthMs), 'month');
    }
    return RELATIVE_TIME_FORMATTER.format(Math.trunc(diffMs / yearMs), 'year');
  };

  const formatAbsoluteDate = (dateString) => {
    if (!dateString) {
      return '-';
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    const includeYear = date.getFullYear() !== new Date().getFullYear();
    const formatter = includeYear ? ABSOLUTE_DATE_FORMATTER_WITH_YEAR : ABSOLUTE_DATE_FORMATTER_CURRENT_YEAR;
    try {
      return formatter.format(date);
    } catch {
      return date.toLocaleString(undefined, {
        ...ABSOLUTE_DATE_FORMATTER_BASE_OPTIONS,
        ...(includeYear ? { year: 'numeric' } : {}),
      });
    }
  };

  const toggleExpanded = (uploadId) => {
    const shouldCollapse = expandedUploadId === uploadId;
    navigate(shouldCollapse ? '/bulkUpload' : `/bulkUpload/${uploadId}`);
  };

  const toggleDateDisplay = (uploadId) => {
    setDateDisplayByUploadId((previous) => ({
      ...previous,
      [uploadId]: previous[uploadId] === 'absolute' ? 'relative' : 'absolute',
    }));
  };

  const handleUploadSubmit = (upload) => {
    if (upload) {
      upsertUpload(upload, { prepend: true });
      navigate(`/bulkUpload/${upload.id}`);
    }
    setUploadPanelOpen(false);
  };

  const handleDownloadError = (message) => {
    setErrorMessage(message);
    setDisplayError(true);
  };

  const closeDownloadMenu = () => {
    setDownloadMenu({ anchorEl: null, uploadId: null });
  };

  const buildDownloadBaseName = (upload) => {
    const fileName = String(upload?.fileName || '').trim();
    if (fileName) {
      const baseName = fileName.replace(/\.[^./\\]+$/, '');
      return baseName || fileName;
    }
    const sourceName = String(upload?.title || upload?.id || '').trim();
    return sourceName || 'bulk-upload';
  };

  const buildResultsFileName = (upload) => `${buildDownloadBaseName(upload)}_results.csv`;

  const buildErrorsFileName = (upload) => `${buildDownloadBaseName(upload)}_errors.csv`;
  const downloadLocalFile = ({
    content,
    fileName,
    contentType = 'text/csv;charset=utf-8;',
  }) => {
    downloadBlobFile({
      content: content ?? '',
      fileName,
      contentType,
    });
  };

  const withDownloadHandling = async ({ upload, kind, run }) => {
    if (!upload?.id || activeDownloadRef.current) {
      return;
    }

    activeDownloadRef.current = { uploadId: upload.id, kind };
    setActiveDownload({ uploadId: upload.id, kind });
    try {
      await run();
    } catch (error) {
      console.error('Unable to download bulk upload file:', error);
      const downloadKindLabel = kind === 'errors'
        ? 'error log'
        : kind === 'archive'
          ? 'archive'
          : 'results';
      handleDownloadError(`Unable to download ${downloadKindLabel}. Please try again.`);
    } finally {
      activeDownloadRef.current = null;
      setActiveDownload({ uploadId: null, kind: null });
    }
  };

  const handleDownloadResults = async (upload) => {
    await withDownloadHandling({
      upload,
      kind: 'results',
      run: async () => {
        const { downloadUrl, fileName } = await resolveBulkUploadResultDownload({
          upload,
          getBulkUploadDetails,
          onDetails: upsertUpload,
          fileNameFallback: buildResultsFileName(upload),
          forceRefresh: true,
        });
        triggerFileDownload({ href: downloadUrl, fileName });
      },
    });
  };

  const handleDownloadErrorLog = async (upload, errorSamples) => {
    await withDownloadHandling({
      upload,
      kind: 'errors',
      run: async () => {
        downloadLocalFile({
          content: buildErrorCsvContent(errorSamples),
          fileName: buildErrorsFileName(upload),
        });
      },
    });
  };

  const handleDownloadArchive = async (upload) => {
    await withDownloadHandling({
      upload,
      kind: 'archive',
      run: async () => {
        const archive = await generateBulkUploadArchive({ uploadId: upload.id });
        triggerFileDownload({
          href: archive.downloadUrl,
          fileName: archive.fileName,
        });
      },
    });
  };

  const isResultsDownloadReady = (upload) => Boolean(upload?.resultDownloadUrl);

  const hasNumericCount = (value) => (
    value !== null
    && value !== undefined
    && value !== ''
    && Number.isFinite(Number(value))
  );
  const getDetailErrorCount = (upload) => (
    toSafeCount(upload?.creationErrorCount) + toSafeCount(upload?.processingErrorCount)
  );
  const hasDetailErrorCountSignal = (upload) => (
    hasNumericCount(upload?.creationErrorCount) || hasNumericCount(upload?.processingErrorCount)
  );
  const getErrorCount = (upload) => (
    hasDetailErrorCountSignal(upload)
      ? getDetailErrorCount(upload)
      : toSafeCount(upload?.errorCount)
  );
  const formatErrorCountLabel = (count) => `${count} ${count === 1 ? 'error' : 'errors'}`;

  const getStatusConfig = (upload) => {
    const normalized = String(upload?.status || '').toLowerCase();
    const totalRows = toSafeCount(upload?.totalRowCount);
    const createdCount = toSafeCount(upload?.createdCount);
    const errorCount = getErrorCount(upload);
    const completedFromDetails = hasNumericCount(upload?.processingCompleteCount)
      ? toSafeCount(upload.processingCompleteCount)
      : null;
    const completedFromSummary = (
      hasNumericCount(upload?.approvedCount)
      && hasNumericCount(upload?.rejectedCount)
      && hasNumericCount(upload?.otherCount)
      && hasDetailErrorCountSignal(upload)
    )
      ? (
        toSafeCount(upload.approvedCount)
        + toSafeCount(upload.rejectedCount)
        + toSafeCount(upload.otherCount)
        + toSafeCount(upload.cancelledCount)
        + errorCount
      )
      : null;
    const completionCandidates = [completedFromDetails, completedFromSummary]
      .filter((value) => value !== null);
    const hasCompletionSignal = completionCandidates.length > 0;
    const completedCount = hasCompletionSignal
      ? Math.max(...completionCandidates)
      : 0;
    const hasCompletedRows = hasCompletionSignal && completedCount > 0;
    const hasContradictoryCompletionCounts = (
      normalized === 'completed'
      && totalRows > 0
      && hasCompletionSignal
      && completedCount < totalRows
    );
    const isComplete = normalized === 'completed'
      ? !hasContradictoryCompletionCounts
      : (totalRows > 0 && hasCompletionSignal && completedCount >= totalRows);
    const hasStartedOrderProcessing = (
      createdCount > 0
      || normalized === 'creating_orders'
      || normalized === 'processing'
      || normalized === 'processing_orders'
    );
    let baseConfig;
    if (normalized === 'failed' || normalized === 'error') {
      baseConfig = { label: 'Failed', tone: 'negative', mode: 'errors' };
    } else if (isComplete) {
      baseConfig = { label: 'Completed', tone: 'positive', mode: 'completed' };
    } else if (hasCompletedRows) {
      baseConfig = { label: 'Verifying', tone: 'info', mode: 'verifying' };
    } else if (hasStartedOrderProcessing) {
      baseConfig = { label: 'Processing Orders', tone: 'warning', mode: 'processing' };
    } else {
      baseConfig = { label: 'Pending', tone: 'neutral', mode: 'pending' };
    }

    if (errorCount > 0) {
      return {
        label: `${baseConfig.label}, with ${formatErrorCountLabel(errorCount)}`,
        tone: 'negative',
        mode: 'errors',
      };
    }

    return baseConfig;
  };

  const getStatusColors = (tone) => {
    switch (tone) {
      case 'positive':
        return {
          color: theme.palette.chipPositive.contrastText,
          dot: theme.palette.chipPositive.contrastText,
          background: theme.palette.chipPositive.main,
        };
      case 'warning':
        return {
          color: theme.palette.emphasis.dark,
          dot: theme.palette.emphasis.dark,
          background: theme.palette.chipWarning.main,
        };
      case 'negative':
        return {
          color: theme.palette.chipNegative.contrastText,
          dot: theme.palette.chipNegative.contrastText,
          background: theme.palette.chipNegative.main,
        };
      case 'info':
        return {
          color: theme.palette.primary.main,
          dot: theme.palette.primary.main,
          background: theme.palette.background.default,
        };
      default:
        return {
          color: theme.palette.text.secondary,
          dot: theme.palette.text.secondary,
          background: theme.palette.grey[200],
        };
    }
  };

  const isPastWithAgentThreshold = (upload) => {
    const timestamp = getBulkUploadTimestamp(upload);
    if (!timestamp) {
      return false;
    }
    return (Date.now() - timestamp) > WITH_AGENT_THRESHOLD_MS;
  };

  const buildVerificationProgressModel = (upload) => {
    const totalRows = toSafeCount(upload?.totalRowCount);
    const total = totalRows;
    if (total === 0) {
      return {
        total: 0,
        submitting: 0,
        verifying: 0,
        withAgent: 0,
        verified: 0,
        rejected: 0,
        cancelled: 0,
        errors: 0,
      };
    }

    const createdRaw = toSafeCount(upload?.createdCount);
    const approvedRaw = toSafeCount(upload?.approvedCount);
    const rejectedRaw = toSafeCount(upload?.rejectedCount);
    const otherRaw = toSafeCount(upload?.otherCount);
    const combinedErrorsRaw = hasDetailErrorCountSignal(upload)
      ? getDetailErrorCount(upload)
      : 0;

    const cancelled = Math.min(total, toSafeCount(upload?.cancelledCount));
    const rejected = Math.min(Math.max(0, total - cancelled), rejectedRaw);
    const errors = Math.min(Math.max(0, total - rejected - cancelled), combinedErrorsRaw);
    const verifiedBase = Math.min(
      Math.max(0, total - rejected - cancelled - errors),
      approvedRaw + otherRaw,
    );
    const completedFromBreakdown = verifiedBase + rejected + cancelled + errors;
    const completedFromDetails = hasNumericCount(upload?.processingCompleteCount)
      ? toSafeCount(upload.processingCompleteCount)
      : 0;
    const completed = Math.min(total, Math.max(completedFromBreakdown, completedFromDetails));
    const verified = verifiedBase + Math.max(0, completed - completedFromBreakdown);

    const hasCreatedCount = hasNumericCount(upload?.createdCount);
    const created = hasCreatedCount
      ? Math.min(total, createdRaw)
      : completed;
    const createdOrCompleted = Math.max(created, completed);
    const remaining = Math.max(0, total - completed);
    const processing = Math.min(remaining, Math.max(0, createdOrCompleted - completed));
    const withAgent = isPastWithAgentThreshold(upload) ? processing : 0;
    const verifying = withAgent > 0 ? 0 : processing;
    const submitting = Math.max(0, remaining - processing);

    return {
      total,
      submitting,
      verifying,
      withAgent,
      verified,
      rejected,
      cancelled,
      errors,
    };
  };

  const tableCellSx = {
    verticalAlign: 'middle',
  };
  const nameColumnCellSx = {
    ...tableCellSx,
    width: '12%',
    minWidth: 110,
    maxWidth: 200,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    overflowWrap: 'normal',
    wordBreak: 'normal',
  };
  const expandedNameColumnCellSx = {
    ...nameColumnCellSx,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  };
  const descriptionColumnCellSx = {
    ...tableCellSx,
    width: '24%',
    minWidth: 240,
    maxWidth: 360,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    overflowWrap: 'normal',
    wordBreak: 'normal',
  };
  const expandedDescriptionColumnCellSx = {
    ...descriptionColumnCellSx,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  };
  const uploadedByColumnCellSx = {
    ...tableCellSx,
    width: '15%',
    minWidth: 140,
    whiteSpace: 'nowrap',
  };
  const dateColumnCellSx = {
    ...tableCellSx,
    width: '16%',
    minWidth: 168,
    whiteSpace: 'nowrap',
  };
  const statusColumnCellSx = {
    ...tableCellSx,
    width: '16%',
    minWidth: 180,
    whiteSpace: 'nowrap',
  };
  const ordersColumnCellSx = {
    ...tableCellSx,
    width: '9%',
    minWidth: 96,
    whiteSpace: 'nowrap',
    textAlign: 'left',
  };
  const actionColumnCellSx = {
    ...tableCellSx,
    width: 72,
    minWidth: 72,
    maxWidth: 72,
    px: 1,
    textAlign: 'right',
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'normal',
  };
  const actionIconSlotSx = {
    width: 32,
    height: 32,
    ml: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const progressSegmentMeta = {
    ...PROGRESS_SEGMENT_META,
    rejected: {
      ...PROGRESS_SEGMENT_META.rejected,
      color: theme.palette.warning?.dark || REJECTED_SEGMENT_COLOR_FALLBACK,
    },
  };

  useEffect(() => {
    setExpandedUploadId(routeUploadId || null);
  }, [routeUploadId]);

  useEffect(() => {
    pendingScrollUploadIdRef.current = expandedUploadId || null;
  }, [expandedUploadId]);

  useEffect(() => {
    const pendingUploadId = pendingScrollUploadIdRef.current;
    if (!pendingUploadId || pendingUploadId !== expandedUploadId) {
      return;
    }
    const node = rowRefs.current.get(pendingUploadId);
    if (!node) {
      return;
    }
    pendingScrollUploadIdRef.current = null;
    window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [expandedUploadId, uploads]);

  const menuUpload = downloadMenu.uploadId
    ? uploads.find((upload) => upload.id === downloadMenu.uploadId) || null
    : null;
  const menuVerificationProgress = menuUpload ? buildVerificationProgressModel(menuUpload) : null;
  const menuCompletionCount = menuVerificationProgress
    ? (
      menuVerificationProgress.verified
      + menuVerificationProgress.rejected
      + menuVerificationProgress.errors
    )
    : 0;
  const menuHasProgressTotal = Boolean(menuVerificationProgress?.total > 0);
  const menuStatus = (menuUpload?.status || '').toLowerCase();
  const menuIsComplete = menuStatus === 'completed'
    || (menuHasProgressTotal && menuCompletionCount >= toSafeCount(menuVerificationProgress?.total));
  const menuCanDownloadResults = Boolean(menuUpload)
    && (isResultsDownloadReady(menuUpload) || menuIsComplete)
    && !Boolean(activeDownload.uploadId);
  const menuCanDownloadErrorLog = Boolean(menuUpload)
    && getErrorSamples(menuUpload).length > 0
    && !Boolean(activeDownload.uploadId);
  const menuCanDownloadArchive = Boolean(menuUpload)
    && typeof generateBulkUploadArchive === 'function'
    && !Boolean(activeDownload.uploadId);
  const downloadMenuItemSx = {
    px: '10px',
    py: '8px',
    minHeight: 'unset',
    fontSize: '1rem',
    lineHeight: '24px',
    color: '#515151',
    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
    },
  };

  return (
    <>
      <ErrorDialog
        open={displayError}
        handleClose={() => setDisplayError(false)}
        message={errorMessage}
      />

      <PrimaryPage
        headerTitle="Bulk Upload"
        infoTitle="Upload Files"
        infoDetail="Upload bulk data files to process multiple records at once. Supported format: CSV."
      >
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setUploadPanelOpen(true)}
            sx={{
              padding: '0.75rem 2rem',
              textTransform: 'none',
              fontSize: '1rem',
              backgroundColor: 'primary.main',
              whiteSpace: 'nowrap',
              ':hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            Upload New File
          </Button>
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
            <Table stickyHeader aria-label="uploaded files table" sx={{ tableLayout: 'auto', minWidth: 1080 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: '600', backgroundColor: 'grey.100', width: 48, ...tableCellSx }} />
                  <TableCell sx={{ fontWeight: '600', backgroundColor: 'grey.100', ...nameColumnCellSx }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: '600', backgroundColor: 'grey.100', ...ordersColumnCellSx }}>
                    Orders
                  </TableCell>
                  <TableCell sx={{ fontWeight: '600', backgroundColor: 'grey.100', ...uploadedByColumnCellSx }}>
                    Uploaded by
                  </TableCell>
                  <TableCell sx={{ fontWeight: '600', backgroundColor: 'grey.100', ...dateColumnCellSx }}>
                    Upload Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: '600', backgroundColor: 'grey.100', ...statusColumnCellSx }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: '600', backgroundColor: 'grey.100', ...descriptionColumnCellSx }}>
                    Description
                  </TableCell>
                  <TableCell sx={{ fontWeight: '600', backgroundColor: 'grey.100', ...actionColumnCellSx }}>
                    <Typography
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: '0.75rem',
                        lineHeight: '18px',
                        fontWeight: 600,
                        color: 'text.secondary',
                        textAlign: 'right',
                      }}
                    >
                      Results
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={tableCellSx}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : uploads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={tableCellSx}>
                      <Typography sx={{ p: 3, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        No files uploaded yet. Click "Upload New File" to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  uploads.map((upload) => {
                    const isExpanded = expandedUploadId === upload.id;
                    const statusConfig = getStatusConfig(upload);
                    const statusColors = getStatusColors(statusConfig.tone);
                    const verificationProgress = buildVerificationProgressModel(upload);
                    const resultsReady = isResultsDownloadReady(upload);
                    const completionCount = verificationProgress.verified
                      + verificationProgress.rejected
                      + verificationProgress.cancelled
                      + verificationProgress.errors;
                    const hasTotalRowCount = hasNumericCount(upload?.totalRowCount);
                    const hasProgressCountFromDetails = hasNumericCount(upload?.processingCompleteCount);
                    const hasProgressCountFromSummary = (
                      hasNumericCount(upload?.approvedCount)
                      && hasNumericCount(upload?.rejectedCount)
                      && hasNumericCount(upload?.otherCount)
                      && hasDetailErrorCountSignal(upload)
                    );
                    const hasCompletionCounts = hasTotalRowCount
                      && (hasProgressCountFromDetails || hasProgressCountFromSummary);
                    const hasDetailMetricsLoaded = hasTotalRowCount
                      && hasNumericCount(upload?.createdCount)
                      && hasDetailErrorCountSignal(upload);
                    const showDetailsLoadingPlaceholder = isExpanded && !hasDetailMetricsLoaded;
                    const completedCountForDisplay = completionCount;
                    const completionCountText = hasCompletionCounts
                      ? `${completedCountForDisplay} / ${toSafeCount(upload.totalRowCount)}`
                      : null;
                    const ordersCountText = hasNumericCount(upload?.totalRowCount)
                      ? `${toSafeCount(upload.totalRowCount)}`
                      : '-';
                    const hasProgressTotal = verificationProgress.total > 0;
                    const hasAnyProgressSegment = PROGRESS_BAR_ORDER
                      .some((segmentKey) => verificationProgress[segmentKey] > 0);
                    const normalizedStatus = (upload.status || '').toLowerCase();
                    const isUploadComplete = normalizedStatus === 'completed'
                      || (hasProgressTotal && completionCount >= verificationProgress.total);
                    const visibleBarSegments = PROGRESS_BAR_ORDER
                      .map((segmentKey) => ({
                        key: segmentKey,
                        ...progressSegmentMeta[segmentKey],
                        count: verificationProgress[segmentKey],
                        isShimmering: !isUploadComplete && SHIMMER_SEGMENT_KEYS.has(segmentKey),
                      }))
                      .filter((segment) => segment.count > 0);
                    const visibleLegendSegments = PROGRESS_LEGEND_ORDER
                      .map((segmentKey) => ({
                        key: segmentKey,
                        ...progressSegmentMeta[segmentKey],
                        count: verificationProgress[segmentKey],
                      }))
                      .filter((segment) => segment.count > 0);
                    const errorSamples = getErrorSamples(upload);
                    const errorLines = buildErrorLines(upload);
                    const totalErrors = getErrorCount(upload);
                    const placeholderCount = errorLines.length === 0 && totalErrors > 0 ? 1 : 0;
                    const remainingErrors = Math.max(0, totalErrors - errorLines.length - placeholderCount);
                    const hasErrors = totalErrors > 0;
                    const hasErrorLogData = errorSamples.length > 0;
                    const additionalNotice = sanitizeErrorMessage(
                      upload?.statusMessage || upload?.processingMessage || upload?.message || '',
                    );
                    const hasNoticePanel = hasErrors || Boolean(additionalNotice);
                    const downloadInProgress = activeDownload.uploadId === upload.id;
                    const downloadsLocked = Boolean(activeDownload.uploadId);
                    const canAttemptResultsLookup = resultsReady || isUploadComplete;
                    const hasArchiveMenuOption = (
                      typeof generateBulkUploadArchive === 'function'
                      && canAttemptResultsLookup
                    );
                    const hasMenuOptions = (
                      (hasErrors && (canAttemptResultsLookup || hasErrorLogData))
                      || hasArchiveMenuOption
                    );
                    const canOpenDownloadMenu = hasMenuOptions && !downloadsLocked;
                    const downloadAriaLabel = hasMenuOptions
                      ? 'Download options'
                      : !canAttemptResultsLookup
                        ? 'Results unavailable'
                        : 'Download results';
                    const areResultsPending = !canAttemptResultsLookup;
                    const canDownloadResults = canAttemptResultsLookup && !downloadsLocked;
                    const canDownloadAction = hasMenuOptions ? canOpenDownloadMenu : canDownloadResults;
                    const canDownloadErrorLog = hasErrors && hasErrorLogData && !downloadsLocked;
                    const isDownloadMenuOpenForRow = downloadMenu.uploadId === upload.id;
                    const triggerResultsDownload = (anchorEl) => {
                      if (downloadsLocked) {
                        return;
                      }
                      if (hasMenuOptions) {
                        setDownloadMenu({ anchorEl, uploadId: upload.id });
                        return;
                      }
                      if (!canAttemptResultsLookup) {
                        return;
                      }
                      void handleDownloadResults(upload);
                    };
                    const triggerErrorLogDownload = () => {
                      if (!canDownloadErrorLog) {
                        return;
                      }
                      void handleDownloadErrorLog(upload, errorSamples);
                    };
                    const relativeDate = formatDate(upload.uploadDate);
                    const absoluteDate = formatAbsoluteDate(upload.uploadDate);
                    const hasValidDate = relativeDate !== '-' && absoluteDate !== '-';
                    const isAbsoluteDate = dateDisplayByUploadId[upload.id] === 'absolute';

                    return (
                      <React.Fragment key={upload.id}>
                        <TableRow
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          hover
                          ref={(node) => {
                            if (node) {
                              rowRefs.current.set(upload.id, node);
                            } else {
                              rowRefs.current.delete(upload.id);
                            }
                          }}
                        >
                          <TableCell padding="checkbox" sx={tableCellSx}>
                            <IconButton
                              aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                              size="small"
                              onClick={() => toggleExpanded(upload.id)}
                            >
                              {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                            </IconButton>
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{
                              fontWeight: '600',
                              ...(isExpanded ? expandedNameColumnCellSx : nameColumnCellSx),
                            }}
                          >
                            {upload.title || upload.fileName || '-'}
                          </TableCell>
                          <TableCell sx={ordersColumnCellSx}>
                            {ordersCountText}
                          </TableCell>
                          <TableCell sx={uploadedByColumnCellSx}>
                            {upload.uploadedBy || '-'}
                          </TableCell>
                          <TableCell sx={dateColumnCellSx}>
                            {!hasValidDate ? '-' : (
                              <Typography
                                component="span"
                                onClick={() => toggleDateDisplay(upload.id)}
                                sx={{
                                  fontSize: '0.875rem',
                                  lineHeight: '1.25rem',
                                  color: 'text.primary',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                }}
                              >
                                {isAbsoluteDate ? absoluteDate : relativeDate}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={statusColumnCellSx}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                              <Box sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.4rem',
                                px: '0.375rem',
                                py: '0.125rem',
                                borderRadius: '80px',
                                color: statusColors.color,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                backgroundColor: 'transparent',
                              }}>
                                {statusConfig.mode === 'errors' ? (
                                  <ErrorIcon
                                    data-testid="bulk-upload-status-error-icon"
                                    sx={{ color: statusColors.dot, fontSize: '0.875rem' }}
                                  />
                                ) : (
                                  <Box sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: statusColors.dot,
                                  }}
                                  />
                                )}
                                {statusConfig.label}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={isExpanded ? expandedDescriptionColumnCellSx : descriptionColumnCellSx}>
                            {upload.description || '-'}
                          </TableCell>
                          <TableCell sx={actionColumnCellSx}>
                            <Box sx={actionIconSlotSx}>
                              <IconButton
                                size="small"
                                aria-label={downloadAriaLabel}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  triggerResultsDownload(event.currentTarget);
                                }}
                                disabled={!canDownloadAction}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  color: canDownloadAction
                                    ? theme.palette.primary.main
                                    : theme.palette.action.disabled,
                                  borderRadius: '4px',
                                  backgroundColor: isDownloadMenuOpenForRow ? '#E6E8F0' : 'transparent',
                                  '&:hover': {
                                    backgroundColor: canDownloadAction ? '#E6E8F0' : 'transparent',
                                  },
                                }}
                              >
                                {downloadInProgress ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <Download sx={{ fontSize: 18 }} />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            sx={{ p: 0, borderBottom: isExpanded ? `1px solid ${theme.palette.divider}` : 'none' }}
                          >
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              {showDetailsLoadingPlaceholder ? (
                                <Box sx={{
                                  px: 3,
                                  py: 2,
                                  backgroundColor: 'background.default',
                                  opacity: 0.6,
                                }}
                                >
                                  <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    borderRadius: '12px',
                                    backgroundColor: 'background.paper',
                                    border: `1px solid ${theme.palette.divider}`,
                                    px: 2,
                                    py: 1.5,
                                  }}
                                  >
                                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: '24px', color: '#212121' }}>
                                      Verification Progress
                                    </Typography>
                                    <LinearProgress
                                      variant="indeterminate"
                                      data-testid="bulk-upload-details-loading-progress"
                                      aria-label="Loading upload details"
                                      sx={{
                                        height: 24,
                                        borderRadius: 1.5,
                                        backgroundColor: theme.palette.grey[300],
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: theme.palette.grey[500],
                                        },
                                      }}
                                    />
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: hasNoticePanel ? 2 : 0,
                                  px: 3,
                                  py: 2,
                                  backgroundColor: 'background.default',
                                }}
                                >
                                <Box sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                  borderRadius: '12px',
                                  backgroundColor: 'background.paper',
                                  border: `1px solid ${theme.palette.divider}`,
                                  px: 2,
                                  py: 1.5,
                                }}
                                >
                                  <Box sx={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                  }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: '24px', color: '#212121' }}>
                                        Verification Progress
                                      </Typography>
                                      <Typography sx={{ fontWeight: 400, fontSize: '0.875rem', lineHeight: '20px', color: '#515151' }}>
                                        {completionCountText
                                          ? `${completionCountText} complete`
                                          : `${completionCount} complete`}
                                      </Typography>
                                    </Box>
                                    {!areResultsPending ? (
                                      <MuiLink
                                        href="#"
                                        onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          triggerResultsDownload(event.currentTarget);
                                        }}
                                        underline="hover"
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          fontSize: '0.8125rem',
                                          lineHeight: '20px',
                                          fontWeight: 500,
                                          color: canDownloadResults ? 'primary.main' : 'text.disabled',
                                          cursor: canDownloadResults ? 'pointer' : 'default',
                                          pointerEvents: canDownloadResults ? 'auto' : 'none',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        <Download sx={{ fontSize: 14 }} />
                                        Download Results
                                      </MuiLink>
                                    ) : (
                                      <Typography
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          fontSize: '0.8125rem',
                                          lineHeight: '20px',
                                          fontWeight: 400,
                                          color: 'text.disabled',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        <Download sx={{ fontSize: 14 }} />
                                        Results available soon
                                      </Typography>
                                    )}
                                  </Box>

                                  {hasProgressTotal && hasAnyProgressSegment ? (
                                    <Box sx={{
                                      display: 'flex',
                                      alignItems: 'stretch',
                                      height: 24,
                                      borderRadius: 1.5,
                                      overflow: 'hidden',
                                      backgroundColor: PROGRESS_TRACK_COLOR,
                                    }}
                                    >
                                      {visibleBarSegments.map((segment) => {
                                        const widthPercent = (segment.count / verificationProgress.total) * 100;
                                        return (
                                          <Box
                                            key={segment.key}
                                            data-testid={`progress-segment-${segment.key}`}
                                            data-shimmer={segment.isShimmering ? 'true' : 'false'}
                                            sx={{
                                              width: `${widthPercent}%`,
                                              minWidth: widthPercent > 0 ? '2px' : 0,
                                              backgroundColor: segment.color,
                                              position: 'relative',
                                              overflow: 'hidden',
                                              ...(segment.isShimmering && {
                                                '&::after': {
                                                  content: '""',
                                                  position: 'absolute',
                                                  inset: 0,
                                                  background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0) 100%)',
                                                  animation: 'bulkUploadShimmer 2s ease-in-out infinite',
                                                },
                                                '@keyframes bulkUploadShimmer': SHIMMER_KEYFRAMES,
                                              }),
                                            }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  ) : (
                                    <LinearProgress
                                      aria-label="Verification progress pending"
                                      sx={{
                                        height: 24,
                                        borderRadius: 1.5,
                                        backgroundColor: PROGRESS_TRACK_COLOR,
                                        '& .MuiLinearProgress-bar': { backgroundColor: progressSegmentMeta.submitting.color },
                                      }}
                                    />
                                  )}

                                  {visibleLegendSegments.length > 0 && (
                                    <Box sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2,
                                      flexWrap: 'wrap',
                                    }}
                                    >
                                      {visibleLegendSegments.map((segment) => (
                                        <Box key={segment.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                          <Box sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '2px',
                                            backgroundColor: segment.color,
                                          }}
                                          />
                                          <Typography sx={{ fontSize: '0.75rem', lineHeight: '18px', color: '#797979' }}>
                                            {segment.label}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.75rem', lineHeight: '18px', fontWeight: 600, color: '#212121' }}>
                                            {segment.count}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                                {hasNoticePanel && (
                                  <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.75,
                                    borderRadius: '8px',
                                    border: hasErrors ? '1px solid #FBD9D8' : `1px solid ${theme.palette.divider}`,
                                    backgroundColor: hasErrors ? '#FFF5F5' : theme.palette.background.paper,
                                    px: 2,
                                    py: 1.5,
                                  }}
                                  >
                                    {hasErrors && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <ErrorIcon
                                          data-testid="bulk-upload-errors-icon"
                                          sx={{ color: '#CF2E2E', fontSize: '1rem' }}
                                        />
                                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#CF2E2E' }}>
                                          {`${totalErrors} ${totalErrors === 1 ? 'order' : 'orders'} could not be processed`}
                                        </Typography>
                                        <MuiLink
                                          href="#"
                                          underline="hover"
                                          onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            triggerErrorLogDownload();
                                          }}
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            fontSize: '0.8125rem',
                                            lineHeight: '20px',
                                            fontWeight: 500,
                                            color: canDownloadErrorLog ? 'primary.main' : 'text.disabled',
                                            cursor: canDownloadErrorLog ? 'pointer' : 'default',
                                            pointerEvents: canDownloadErrorLog ? 'auto' : 'none',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          <Download sx={{ fontSize: 14 }} />
                                          Download errors
                                        </MuiLink>
                                      </Box>
                                    )}
                                    {errorLines.length > 0 ? errorLines.map((errorLine) => (
                                      <Typography
                                        key={errorLine.key}
                                        sx={{
                                          color: 'text.secondary',
                                          fontWeight: 400,
                                          fontSize: '0.75rem',
                                          lineHeight: '18px',
                                          whiteSpace: 'normal',
                                          overflowWrap: 'anywhere',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {errorLine.text}
                                      </Typography>
                                    )) : (
                                      hasErrors && (
                                        <Typography
                                          sx={{
                                            color: 'text.secondary',
                                            fontSize: '0.75rem',
                                            lineHeight: '18px',
                                          }}
                                        >
                                          Error details pending.
                                        </Typography>
                                      )
                                    )}
                                    {remainingErrors > 0 && (
                                      <Typography
                                        sx={{
                                          color: 'text.secondary',
                                          fontSize: '0.75rem',
                                          lineHeight: '18px',
                                        }}
                                      >
                                        {remainingErrors} more errors
                                      </Typography>
                                    )}
                                    {additionalNotice && (
                                      <Typography
                                        sx={{
                                          color: 'text.secondary',
                                          fontSize: '0.75rem',
                                          lineHeight: '18px',
                                          whiteSpace: 'normal',
                                          overflowWrap: 'anywhere',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {additionalNotice}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                                </Box>
                              )}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {hasMore && !isLoading && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: 'background.default',
            }}>
              <Button
                variant="outlined"
                onClick={loadMore}
                disabled={isLoadingMore}
                sx={{ textTransform: 'none' }}
              >
                {isLoadingMore ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    Loading...
                  </Box>
                ) : (
                  'Load more'
                )}
              </Button>
            </Box>
          )}
        </Paper>
        <Menu
          anchorEl={downloadMenu.anchorEl}
          open={Boolean(downloadMenu.anchorEl)}
          onClose={closeDownloadMenu}
          PaperProps={{
            elevation: 0,
            sx: {
              minWidth: 207,
              backgroundColor: '#FAFAFA',
              borderRadius: '8px',
              boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.3), 0px 4px 8px 3px rgba(0,0,0,0.15)',
              p: '2px',
            },
          }}
          MenuListProps={{
            disablePadding: true,
          }}
        >
          <MenuItem
            onClick={() => {
              if (!menuUpload || !menuCanDownloadResults) {
                closeDownloadMenu();
                return;
              }
              closeDownloadMenu();
              void handleDownloadResults(menuUpload);
            }}
            disabled={!menuCanDownloadResults}
            sx={downloadMenuItemSx}
          >
            Download results (.csv)
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!menuUpload || !menuCanDownloadErrorLog) {
                closeDownloadMenu();
                return;
              }
              closeDownloadMenu();
              void handleDownloadErrorLog(menuUpload, getErrorSamples(menuUpload));
            }}
            disabled={!menuCanDownloadErrorLog}
            sx={downloadMenuItemSx}
          >
            Download error log (.csv)
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!menuUpload || !menuCanDownloadArchive) {
                closeDownloadMenu();
                return;
              }
              closeDownloadMenu();
              void handleDownloadArchive(menuUpload);
            }}
            disabled={!menuCanDownloadArchive}
            sx={downloadMenuItemSx}
          >
            Download all, plus original upload (.zip)
          </MenuItem>
        </Menu>
      </PrimaryPage>

      <Drawer
        anchor="right"
        open={uploadPanelOpen}
        onClose={() => setUploadPanelOpen(false)}
        PaperProps={{ sx: { width: '480px' } }}
      >
        <BulkUploadUploadPanel
          onCancel={() => setUploadPanelOpen(false)}
          onSubmit={handleUploadSubmit}
        />
      </Drawer>
    </>
  );
};

export default BulkUpload;
